"use client";

import { FeedEvents, UserEvents, trackEvent } from "@/analytics";

import ClapReactionButton from "@/components/special/ClapReactionButton";
import { Comedian } from "@/data/comedians";
import { CommentCount } from "@/components/comments";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import FeedCard from "./FeedCard";
import { FeedPost } from "../types";
import { Follow } from "@/components/checkout";
import HideButton from "@/components/common/HideButton";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import VideoSkeleton from "@punchup/ui-common/components/common/VideoSkeleton";

const LiveVideoPlayerAndWaitingRoom = dynamic(
  () =>
    import("@/components/livestreaming/LiveVideoPlayerAndWaitingRoom").then(
      (mod) => mod.LiveVideoPlayerAndWaitingRoom
    ),
  { ssr: false, loading: () => <VideoSkeleton /> }
);
import { Livestream } from "@punchup/data/legacy/livestreaming";
import ShareButton from "@/components/common/ShareButton";
import { SpecialRow } from "@/data/specials";
import VideoFeedHeaderDesktop from "./VideoFeedHeaderDesktop";
import { VideoJS, VideoPlayerSurface } from "@punchup/video/client";
import { cn } from "@/components/common/utils";
import dayjs from "@punchup/utils/dayjs";
import { memo } from "react";
import { useIsMobile } from "@/components/common/hooks";

interface VideoFeedCardHorizontalProps {
  post: FeedPost;
  special: SpecialRow;
  comedian: Comedian;
  userFollowsComedian: boolean;
  isLoggedOutState: boolean;
  muted: boolean;
  handleMutedChange: (value: boolean) => void;
  playing: boolean;
  videoRefCallback: (element: HTMLDivElement) => (() => void) | void;
  // Data props
  videoSrc: string;
  profileImg: string;
  previewUrl: string | null;
  previewImg: string | null;
  userSubscribedToComedian: boolean | null;
  livestream: Livestream | null;
  userPurchasedSpecial: boolean | null;
  subscriptionPrice: number | null | undefined;
  // Computed values
  posterImg: string;
  specialImg: string;
  userHasAccessToSpecial: boolean;
  isCommentsEnabled: boolean;
  followCallback: () => Promise<void>;
  renderTimestamp: (postAge: number) => string;
  postAge: number;
  specialUrl: string;
  redirectTo: string;
  hideEnabled?: boolean;
  onHide?: (postId: string) => void;
}

function VideoFeedCardHorizontal({
  post,
  special,
  comedian,
  userFollowsComedian,
  isLoggedOutState,
  muted,
  handleMutedChange,
  playing,
  videoRefCallback,
  videoSrc,
  profileImg,
  previewUrl,
  previewImg,
  userSubscribedToComedian,
  livestream,
  userPurchasedSpecial,
  subscriptionPrice,
  posterImg,
  specialImg,
  userHasAccessToSpecial,
  isCommentsEnabled,
  followCallback,
  renderTimestamp,
  postAge,
  specialUrl,
  redirectTo,
  hideEnabled = false,
  onHide,
}: VideoFeedCardHorizontalProps) {
  const isMobile = useIsMobile();
  const isVerticalVideo = special.is_vertical === true;

  return (
    <FeedCard variant="horizontal-video">
      <div className="flex flex-col gap-2">
        {/* Desktop header - always shown for horizontal videos */}
        <VideoFeedHeaderDesktop
          comedian={comedian}
          special={special}
          postAge={postAge}
          profileImg={profileImg}
          userFollowsComedian={userFollowsComedian}
          userSubscribedToComedian={userSubscribedToComedian}
          subscriptionPrice={subscriptionPrice}
          isLoggedOutState={isLoggedOutState}
          redirectTo={redirectTo}
          renderTimestamp={renderTimestamp}
          taggedComedians={post.taggedComedians}
        />

        <div>
          <div
            className={cn(
              "flex flex-col",
              special.description ? "gap-5" : "gap-2"
            )}
          >
            {/* Horizontal video container */}
            <div
              className={cn(
                "relative overflow-hidden md:rounded-md",
                "mt-1 md:mt-1.5",
                // Add black background for desktop vertical videos to show letterboxing
                isVerticalVideo && !isMobile && "bg-black"
              )}
              key={
                special.requires_purchase && userPurchasedSpecial
                  ? "videoSrc"
                  : !special.requires_purchase && userFollowsComedian
                    ? "videoSrc"
                    : "previewUrl"
              }
              ref={videoRefCallback}
            >
              {livestream && post.livestream?.signedVideoToken ? (
                <LiveVideoPlayerAndWaitingRoom
                  special={special}
                  livestream={livestream}
                  placeholderImageUrl={specialImg}
                  signedToken={post.livestream?.signedVideoToken}
                  allowStreamTools={false}
                />
              ) : !previewUrl ? (
                <Image
                  src={specialImg}
                  alt={`${special.title} by ${comedian.display_name}`}
                  width={798}
                  height={462}
                  className="w-full h-full max-h-[460px] relative md:rounded-lg aspect-video object-cover bg-gray-300 z-20"
                />
              ) : userHasAccessToSpecial ? (
                <VideoJS
                  bigPlayButton={false}
                  special={special}
                  poster={posterImg}
                  context={{ comedian, recommendedVideos: [] }}
                  lastPlaybackLocation={0}
                  onSpecialChange={() => {}}
                  src={videoSrc}
                  isPreview={false}
                  surface={VideoPlayerSurface.Feed}
                  inline
                  disableOverlays
                  muted={muted}
                  handleMutedChange={handleMutedChange}
                  playing={playing}
                  isVertical={false}
                  className="w-full h-full"
                  playerProps={{
                    showSkipButtons: false,
                    showPlaybackRate: false,
                    showVolumePanel: false,
                    showFullscreenToggle: false,
                    showTimeRemaining: false,
                  }}
                />
              ) : previewUrl && previewImg ? (
                <VideoJS
                  bigPlayButton={false}
                  special={special}
                  poster={previewImg ?? ""}
                  context={{ comedian, recommendedVideos: [] }} // TODO: This should include isPreview:true but doing so breaks the feed auto play
                  lastPlaybackLocation={0}
                  onSpecialChange={() => {}}
                  src={previewUrl ?? ""}
                  isPreview={true}
                  surface={VideoPlayerSurface.Feed}
                  inline
                  disableOverlays
                  muted={muted}
                  handleMutedChange={handleMutedChange}
                  playing={playing}
                  isVertical={false}
                  className="w-full h-full"
                  playerProps={{
                    showSkipButtons: false,
                    showPlaybackRate: false,
                    showVolumePanel: false,
                    showFullscreenToggle: false,
                    showTimeRemaining: false,
                  }}
                />
              ) : null}
            </div>

            <div
              className={cn("flex flex-col gap-2.5 md:gap-3 px-4 pt-1 md:px-0")}
            >
              <div className={cn("flex flex-row justify-between gap-2")}>
                <div
                  className={cn(
                    "flex flex-col",
                    !userFollowsComedian ? "justify-start" : "justify-center"
                  )}
                >
                  <div className="inline-flex gap-2">
                    <Link
                      href={specialUrl}
                      onClick={() =>
                        trackEvent(FeedEvents.CLICKED_TITLE_VIDEO_CARD)
                      }
                      className="hover:underline hover:underline-offset-2 duration-200 ease-in-out decoration-[transparent] hover:decoration-black transition-[text-decoration]"
                      data-testid="video-feed-card-title-link"
                    >
                      <h2 className="font-sans text-xl font-semibold md:text-2xl line-clamp-2">
                        {special.title}
                      </h2>
                    </Link>
                  </div>

                  {!userFollowsComedian && (
                    <div className="flex flex-col items-start gap-2 pt-1 md:items-center md:flex-row">
                      {isLoggedOutState ? (
                        <span
                          className={cn(
                            "text-[#595959] flex items-center text-[12px] md:text-[14px]"
                          )}
                        >
                          <span className="mr-1 md:hidden">Tap</span>
                          <Follow
                            redirectTo={redirectTo}
                            buttonText="Subscribe"
                            followedLocation={FOLLOW_LOCATIONS.FEED_VIDEO_CARD}
                            isLoggedOutState={isLoggedOutState}
                            comedianToFollow={comedian}
                            customCSSButtonClass="font-medium text-primary mr-1 hover:underline"
                            customCSSContainerClass="inline"
                            additionalEventToTrack={{
                              event: UserEvents.CLICKED_UNLOCK_SPECIAL,
                              eventProperties: {
                                comedianId: comedian.id,
                                comedianName: comedian.display_name,
                                location: FOLLOW_LOCATIONS.FEED_VIDEO_CARD,
                              },
                            }}
                          />
                          {" to access the full video"}
                        </span>
                      ) : (
                        <p className="text-[14px] text-[#595959]">
                          <span className="md:hidden">Tap</span>
                          <span className="hidden md:inline-block">
                            Click
                          </span>{" "}
                          <span className="font-medium text-primary">
                            Subscribe
                          </span>{" "}
                          to access the full video
                        </p>
                      )}
                    </div>
                  )}

                  {livestream && (
                    <p className="text-[14px] text-[#595959]">
                      {livestream.state === "live" ? (
                        <>
                          <span className="font-black">Live now!</span>{" "}
                          <Link
                            href={`/${comedian.slug}/${special.slug}`}
                            className="font-medium text-primary"
                          >
                            Click here to watch live
                          </Link>
                        </>
                      ) : livestream.state === "ended" ? (
                        <span>This live stream has ended</span>
                      ) : livestream.start_time == null ? (
                        <span className="font-medium">Starting soon!</span>
                      ) : (
                        <>
                          <span className="">Scheduled to start</span>{" "}
                          <span className="">
                            {dayjs(livestream.start_time).format(
                              "MMM D, h:mm A z"
                            )}
                          </span>
                        </>
                      )}
                    </p>
                  )}
                </div>

                <div className="flex flex-col justify-start">
                  {!userHasAccessToSpecial && (
                    <div className="flex flex-row items-center text-[12px] text-neutral-500 font-medium px-1.5 py-0.5 border border-neutral-400/90 rounded-md mb-1">
                      <p>Preview</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-row justify-between gap-2">
                <div className="flex items-center gap-3">
                  <ClapReactionButton
                    special={special}
                    comedian={comedian}
                    authenticated={!isLoggedOutState}
                    className="h-[36px]"
                  />
                  {isCommentsEnabled && (
                    <CommentCount
                      videoId={special.id}
                      showAvatars={true}
                      className="text-sm"
                      href={`/${comedian.slug}/${special.slug}`}
                    />
                  )}
                  <ShareButton
                    iconColor="stroke-[#3F3F3F]"
                    className="h-[36px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
                    label="Share"
                    shareData={{
                      title: comedian.display_name || "",
                      text: `Check out ${comedian.display_name} on Punchup!`,
                      url: specialUrl,
                    }}
                    location="feed-video-card"
                    iconHeight={18}
                    iconWidth={18}
                  />
                  {hideEnabled && (
                    <HideButton
                      specialId={special.id}
                      postId={post.id}
                      onHide={onHide}
                      location="feed-video-card"
                      label="Hide"
                      iconColor="stroke-[#3F3F3F]"
                      className="h-[36px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
                      iconHeight={18}
                      iconWidth={18}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FeedCard>
  );
}

export default memo(VideoFeedCardHorizontal);
